import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import { BackButton, FormField, FormGrid, FormSection, InlineFeedback, PrimaryButton } from 'components/forms';
import { useFeedback } from 'contexts/FeedbackContext';
import type React from 'react';
import { useMemo, useState } from 'react';
import {
  useGetClientDocumentsQuery,
  useGetInsurexSaleOptionsQuery,
  useUpdateClientSaleMutation,
} from 'services/api/clients.api';
import type { Document } from 'services/types/document';
import type { InsuranceData, LoanData, Sale } from 'services/types/sale';

interface SaleEditFormProps {
  clientId: string;
  sale: Sale;
  onCancel: () => void;
  onSaved: () => void;
}

interface FormState {
  loanInstitutionName: string;
  loanNumber: string;
  totalAskedAmount: string;
  pendingBalance: string;
  remainingQuotas: string;
  installmentValue: string;
  policyNumber: string;
  currentInstitution: string;
  insuranceCost: string;
  refundAmount: string;
  documentIds: number[];
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  identity_card: 'Cédula de Identidad',
  loan: 'Crédito',
  insurance_policy: 'Póliza de Seguro',
  bank_statement: 'Cartola Bancaria',
  debt_settlement_report: 'Liquidación de Deudas',
  salary_settlement_report: 'Liquidación de Salario',
  cmf_debt_report: 'Reporte CMF',
  afp_contribution_certificate: 'Certificado de Cotizaciones AFP',
  utility_bill: 'Boleta de Servicio',
  additional_document: 'Documento Adicional',
  other: 'Otro',
};

const documentLabel = (doc: Document): string => {
  const typeLabel = doc.type ? (DOCUMENT_TYPE_LABELS[doc.type] ?? doc.type) : 'Documento';
  const name = doc.aiName ? ` — ${doc.aiName}` : '';
  return `${typeLabel}${name} (#${doc.id})`;
};

const toStr = (v: number | string | null | undefined): string => {
  if (v === null || v === undefined) return '';
  return String(v);
};

const parseNumber = (v: string): number | undefined => {
  const trimmed = v.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
};

const parseString = (v: string): string | undefined => {
  const trimmed = v.trim();
  return trimmed === '' ? undefined : trimmed;
};

const initialState = (sale: Sale): FormState => ({
  loanInstitutionName: sale.loanData?.institutionName ?? '',
  loanNumber: sale.loanData?.loanNumber ?? '',
  totalAskedAmount: toStr(sale.loanData?.totalAskedAmount),
  pendingBalance: toStr(sale.loanData?.pendingBalance),
  remainingQuotas: toStr(sale.loanData?.remainingQuotas),
  installmentValue: toStr(sale.loanData?.installmentValue),
  policyNumber: sale.insuranceData?.policyNumber ?? '',
  currentInstitution: sale.insuranceData?.currentInstitution ?? '',
  insuranceCost: toStr(sale.insuranceData?.insuranceCost),
  refundAmount: toStr(sale.insuranceData?.refundAmount),
  documentIds: sale.documentIds ?? [],
});

const SaleEditForm: React.FC<SaleEditFormProps> = ({ clientId, sale, onCancel, onSaved }) => {
  const [state, setState] = useState<FormState>(() => initialState(sale));
  const [updateSale, { isLoading, error }] = useUpdateClientSaleMutation();
  const [crmError, setCrmError] = useState<string | null>(null);
  const { notifySaved } = useFeedback();
  const { data: saleOptions } = useGetInsurexSaleOptionsQuery(undefined);
  const { data: clientDocuments } = useGetClientDocumentsQuery(clientId);
  const loanInstitutions = saleOptions?.loanInstitutions ?? [];
  const insuranceCompanies = saleOptions?.insuranceCompanies ?? [];
  const documentsById = new Map((clientDocuments ?? []).map((doc) => [doc.id, doc]));

  const setField =
    <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setState((prev) => ({ ...prev, [key]: e.target.value as FormState[K] }));
    };

  const missingSaleFieldsForCrm = useMemo(() => {
    const missing: string[] = [];
    if (!sale.insuranceType) missing.push('Tipo de Oportunidad');
    if (!state.loanNumber.trim()) missing.push('Número de Operación');
    if (parseNumber(state.totalAskedAmount) == null) missing.push('Monto Solicitado');
    if (parseNumber(state.pendingBalance) == null) missing.push('Monto por Pagar');
    if (parseNumber(state.remainingQuotas) == null) missing.push('Cuotas por Pagar');
    if (parseNumber(state.refundAmount) == null) missing.push('Monto de Devolución');
    return missing;
  }, [
    sale.insuranceType,
    state.loanNumber,
    state.totalAskedAmount,
    state.pendingBalance,
    state.remainingQuotas,
    state.refundAmount,
  ]);

  const setDocumentIds = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value as unknown;
    const values = Array.isArray(raw) ? raw : [];
    const ids = values.map((v) => Number(v)).filter((n) => Number.isFinite(n));
    setState((prev) => ({ ...prev, documentIds: ids }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const loanData: LoanData = {
      ...(sale.loanData ?? {}),
      institutionName: parseString(state.loanInstitutionName),
      loanNumber: parseString(state.loanNumber),
      totalAskedAmount: parseNumber(state.totalAskedAmount),
      pendingBalance: parseNumber(state.pendingBalance),
      remainingQuotas: parseNumber(state.remainingQuotas),
      installmentValue: parseNumber(state.installmentValue) ?? null,
    };

    const insuranceData: InsuranceData = {
      ...(sale.insuranceData ?? {}),
      policyNumber: parseString(state.policyNumber),
      currentInstitution: parseString(state.currentInstitution),
      insuranceCost: parseNumber(state.insuranceCost),
      refundAmount: parseNumber(state.refundAmount),
    };

    try {
      const result = await updateSale({
        clientId,
        saleId: sale.id,
        body: {
          loanData,
          insuranceData,
          documentIds: state.documentIds,
        },
      }).unwrap();
      if (result.crmSync === 'failed' && result.crmError) {
        // Sale saved locally, but CRM sync failed — keep form open and show
        // the CRM error inline. The user can fix and save again.
        notifySaved('Cambios guardados localmente');
        setCrmError(result.crmError);
        return;
      }
      setCrmError(null);
      notifySaved(result.crmSync === 'updated' ? 'Cambios guardados y sincronizados con CRM' : 'Cambios guardados');
      onSaved();
    } catch {
      // error surfaced below via `error`
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      {missingSaleFieldsForCrm.length > 0 && (
        <InlineFeedback severity="warning" sx={{ mb: 2 }}>
          Para subir esta venta al CRM completa: {missingSaleFieldsForCrm.join(', ')}.
        </InlineFeedback>
      )}
      <FormSection ghost dense title="Información del crédito">
        <FormGrid columns={2} gap={2.5}>
          <FormField
            select
            label="Institución"
            value={state.loanInstitutionName}
            onChange={setField('loanInstitutionName')}
          >
            <MenuItem value="" sx={{ fontFamily: 'Jost, sans-serif' }}>
              <em>{loanInstitutions.length === 0 ? 'Cargando…' : 'Sin definir'}</em>
            </MenuItem>
            {loanInstitutions.map((name) => (
              <MenuItem key={name} value={name} sx={{ fontFamily: 'Jost, sans-serif' }}>
                {name}
              </MenuItem>
            ))}
          </FormField>
          <FormField label="Número de crédito" value={state.loanNumber} onChange={setField('loanNumber')} />
          <FormField
            thousandSeparator
            label="Monto total solicitado"
            value={state.totalAskedAmount}
            onChange={setField('totalAskedAmount')}
          />
          <FormField
            thousandSeparator
            label="Saldo pendiente"
            value={state.pendingBalance}
            onChange={setField('pendingBalance')}
          />
          <FormField
            type="number"
            label="Cuotas restantes"
            value={state.remainingQuotas}
            onChange={setField('remainingQuotas')}
          />
          <FormField
            thousandSeparator
            label="Valor de la cuota"
            value={state.installmentValue}
            onChange={setField('installmentValue')}
          />
        </FormGrid>
      </FormSection>

      <FormSection ghost dense title="Información del seguro">
        <FormGrid columns={2} gap={2.5}>
          <FormField
            select
            label="Aseguradora actual"
            value={state.currentInstitution}
            onChange={setField('currentInstitution')}
          >
            <MenuItem value="" sx={{ fontFamily: 'Jost, sans-serif' }}>
              <em>{insuranceCompanies.length === 0 ? 'Cargando…' : 'Sin definir'}</em>
            </MenuItem>
            {insuranceCompanies.map((name) => (
              <MenuItem key={name} value={name} sx={{ fontFamily: 'Jost, sans-serif' }}>
                {name}
              </MenuItem>
            ))}
          </FormField>
          <FormField label="Número de póliza" value={state.policyNumber} onChange={setField('policyNumber')} />
          <FormField
            thousandSeparator
            label="Costo del seguro"
            value={state.insuranceCost}
            onChange={setField('insuranceCost')}
          />
        </FormGrid>
      </FormSection>

      <FormSection ghost dense title="Documentos asociados">
        <FormField
          select
          label="Documentos del cliente"
          hint={
            clientDocuments && clientDocuments.length > 0
              ? 'Selecciona los documentos del cliente que quieres asociar a esta portabilidad.'
              : 'Este cliente todavía no tiene documentos cargados.'
          }
          value={state.documentIds}
          onChange={setDocumentIds}
          disabled={!clientDocuments || clientDocuments.length === 0}
          slotProps={{
            select: {
              multiple: true,
              renderValue: (selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as number[]).map((id) => {
                    const doc = documentsById.get(id);
                    return <Chip key={id} size="small" label={doc ? documentLabel(doc) : `#${id}`} />;
                  })}
                </Box>
              ),
            },
          }}
        >
          {(clientDocuments ?? []).map((doc) => (
            <MenuItem key={doc.id} value={doc.id} sx={{ fontFamily: 'Jost, sans-serif' }}>
              <Checkbox checked={state.documentIds.includes(doc.id)} size="small" />
              <ListItemText primary={documentLabel(doc)} />
            </MenuItem>
          ))}
        </FormField>
      </FormSection>

      <FormSection ghost dense title="Simulación">
        <FormGrid columns={2} gap={2.5}>
          <FormField
            thousandSeparator
            label="Reembolso"
            value={state.refundAmount}
            onChange={setField('refundAmount')}
          />
        </FormGrid>
      </FormSection>

      {crmError && (
        <InlineFeedback severity="error" sx={{ mb: 2 }}>
          Los cambios se guardaron localmente, pero la sincronización con el CRM falló. Intenta más tarde.
        </InlineFeedback>
      )}

      {error && (
        <InlineFeedback severity="error" sx={{ mb: 2 }}>
          Ocurrió un error al guardar los cambios. Intenta más tarde.
        </InlineFeedback>
      )}

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
        <BackButton onClick={onCancel} disabled={isLoading} type="button">
          Cancelar
        </BackButton>
        <PrimaryButton type="submit" disabled={isLoading}>
          {isLoading ? 'Guardando…' : 'Guardar'}
        </PrimaryButton>
      </Box>
    </Box>
  );
};

export default SaleEditForm;
